import { Badge } from './ui/badge';
import { Ban, CheckCheck, Eye, Hammer, Lock, Search } from 'lucide-react';
import { labelFor, normalizeStatus, type LifecycleStatus } from '../utils/configItemLifecycle';

/**
 * ConfigItemStatusBadge: a config item's one lifecycle status (docs/CONFIG_ITEM_LIFECYCLE.md).
 * Takes the item (legacy spellings and the old implementationStatus fold) or a status string.
 */

interface ConfigItemStatusBadgeProps {
  item?: { status?: unknown; implementationStatus?: unknown } | null;
  status?: string | null;
  size?: 'sm' | 'default';
}

const STYLE: Record<LifecycleStatus, { icon: typeof Search; className: string; variant: 'outline' | 'default' }> = {
  discovery: { icon: Search, className: 'text-muted-foreground border-muted-foreground/50', variant: 'outline' },
  ready_for_build: { icon: Lock, className: 'bg-blue-600 hover:bg-blue-700', variant: 'default' },
  in_build: { icon: Hammer, className: 'bg-orange-500 hover:bg-orange-600', variant: 'default' },
  ready_for_review: { icon: Eye, className: 'bg-purple-600 hover:bg-purple-700', variant: 'default' },
  approved: { icon: CheckCheck, className: 'bg-green-600 hover:bg-green-700', variant: 'default' },
  blocked: { icon: Ban, className: 'bg-red-600 hover:bg-red-700', variant: 'default' },
};

export function ConfigItemStatusBadge({ item, status, size = 'default' }: ConfigItemStatusBadgeProps) {
  const lifecycle = item ? normalizeStatus(item) : normalizeStatus(status ?? undefined);
  const { icon: Icon, className, variant } = STYLE[lifecycle];
  return (
    <Badge variant={variant} className={`${className} ${size === 'sm' ? 'text-xs' : ''}`} data-status={lifecycle}>
      <Icon className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} aria-hidden="true" />
      {labelFor(lifecycle)}
    </Badge>
  );
}
